import {
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { IVoucherLimitState } from "@saleor/discounts/hooks/useVoucherLimitsData";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import useAppState from "@saleor/hooks/useAppState";
import { commonMessages } from "@saleor/intl";
import { getFormErrors } from "@saleor/utils/errors";
import { runVoucherValidation } from "@saleor/utils/validators/voucher";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { generateCode } from "../../../misc";
import { VoucherDetailsPageFormData } from "../VoucherDetailsPage";

interface VoucherInfoProps {
  data: VoucherDetailsPageFormData;
  isBulkVoucher?: boolean;
  clientErrors?: {
    code: string;
  };
  errors: DiscountErrorFragment[];
  disabled: boolean;
  variant: "create" | "update";
  onChange: (event: any) => void;
}

const VoucherInfo = ({
  data,
  disabled,
  errors,
  isBulkVoucher,
  clientErrors,
  variant,
  onChange
}: VoucherInfoProps) => {
  const intl = useIntl();
  const formErrors = getFormErrors(["code"], errors);

  const handleNumberChange = (e: any) => {
    if (/^[.0-9\b]*$/.test(e.target.value)) {
      onChange(e);
    }
  };

  const onGenerateCode = () =>
    onChange({
      target: {
        name: "code",
        value: generateCode(10)
      }
    });

  const [, dispatchAppState] = useAppState();

  React.useEffect(() => {
    dispatchAppState({
      payload: {
        error: undefined
      },
      type: "displayError"
    });
    if (isBulkVoucher && data.bulkCreate) {
      onChange({
        target: {
          name: "bulkCreate",
          value: false
        }
      });
    }
  }, [data.bulkCreate, isBulkVoucher]);

  const localValidation = runVoucherValidation(
    data,
    false,
    {} as IVoucherLimitState
  );

  const getFilteredLocalError = () => {
    if (
      data.code.length === 0 &&
      localValidation.code === "This field is required"
    ) {
      return null;
    }
    return localValidation.code;
  };

  const activeErrorMessage =
    (formErrors.code && formErrors.code.message) ||
    (clientErrors?.code ? localValidation.code : getFilteredLocalError());

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage(commonMessages.generalInformations)}
        toolbar={
          variant === "create" && (
            <Button
              color="primary"
              onClick={onGenerateCode}
              id="generateCodeBtn"
            >
              <FormattedMessage
                defaultMessage="Generate Code"
                description="voucher code, button"
              />
            </Button>
          )
        }
      />
      <CardContent>
        {data?.bulkCreate ? (
          <TextField
            disabled={disabled || isBulkVoucher}
            id="voucherCodeTxt"
            error={!!activeErrorMessage}
            helperText={activeErrorMessage}
            fullWidth
            name={"code" as keyof VoucherDetailsPageFormData}
            label={intl.formatMessage({
              defaultMessage: "Voucher Code"
            })}
            value={data.code}
            onChange={e => {
              const sanitizedValue = e.target.value.replace(/\s/g, "");
              onChange({
                ...e,
                target: {
                  ...e.target,
                  name: "code",
                  value: sanitizedValue
                }
              });
            }}
            inputProps={{
              maxLength: 12
            }}
            InputProps={{
              endAdornment: data.bulkCreate ? (
                <InputAdornment position="end">-00000000</InputAdornment>
              ) : (
                ""
              )
            }}
          />
        ) : (
          <TextField
            disabled={disabled || isBulkVoucher}
            id="voucherCodeTxt"
            error={!!activeErrorMessage}
            helperText={activeErrorMessage}
            fullWidth
            name={"code" as keyof VoucherDetailsPageFormData}
            label={intl.formatMessage({
              defaultMessage: "Voucher Code"
            })}
            value={data.code}
            onChange={onChange}
            inputProps={{
              maxLength: 16
            }}
            InputProps={{
              endAdornment: data.bulkCreate ? (
                <InputAdornment position="end">-0000</InputAdornment>
              ) : (
                ""
              )
            }}
          />
        )}

        <CardSpacer />
        <div style={{ display: `${variant === "update" ? "none" : "block"}` }}>
          <Grid container>
            <Grid item xs={3}>
              <FormControlLabel
                value={data.bulkCreate}
                control={
                  <Switch
                    disabled={isBulkVoucher || disabled}
                    id="bulkCreateToggle"
                    color="primary"
                    checked={data.bulkCreate}
                    // onChange={event => setChecked(event.target.checked)}
                    onChange={event => {
                      onChange({
                        target: {
                          name: "bulkCreate",
                          value: event.target.checked
                        }
                      });
                      onChange({
                        target: {
                          name: "code",
                          value: ""
                        }
                      });
                    }}
                  />
                }
                label={intl.formatMessage({
                  defaultMessage: "Bulk Create"
                })}
                labelPlacement="start"
              />
            </Grid>
            <Grid item xs={9}>
              {data.bulkCreate ? (
                <TextField
                  disabled={disabled || isBulkVoucher}
                  label={intl.formatMessage({
                    defaultMessage: "Total Copies "
                  })}
                  name="multiplier"
                  fullWidth
                  onChange={handleNumberChange}
                  value={data.multiplier || ""}
                  inputProps={{
                    maxLength: 4,
                    min: 2
                  }}
                />
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </div>
      </CardContent>
    </Card>
  );
};

export default VoucherInfo;
